/**
 * rtIsodoseMapCore.js — проекция изодоз на плоскость MPR-окна, чистая часть.
 *
 * Вынесено из rtDoseModule.drawIsodoses: базис плоскости из камеры, шаг и
 * размер сетки выборки, перевод мировых изолиний в канвенные полилинии через
 * ИНЖЕКТИРУЕМЫЙ worldToCanvas, цвета уровней. Модулю остаётся SVG и события.
 *
 * [7.0.23] Заодно закрыт бесшумный отказ: при камере с viewUp ∥ viewPlaneNormal
 * (бывает после сбоя раскладки) cross давал нулевой вектор, _norm возвращал
 * нули — и изодозы молча не рисовались. Теперь базис достраивается от запасной
 * оси, и рисуется всегда.
 */

import { isodoseOnPlane, doseGridPixelToWorld, frameSpacing } from './rtDoseCore.js';

const _cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const _len = (v) => Math.hypot(v[0], v[1], v[2]);
const _norm = (v) => { const n = _len(v) || 1; return [v[0] / n, v[1] / n, v[2] / n]; };

/**
 * Ортонормальный базис плоскости из камеры.
 * @param {{focalPoint:number[], viewUp?:number[], viewPlaneNormal?:number[]}} cam
 * @returns {{o:number[], uDir:number[], vDir:number[]}}
 */
export function planeBasisFromCamera(cam) {
  const o = cam.focalPoint;
  const nrm = _norm(cam.viewPlaneNormal || [0, 0, 1]);
  let up = _norm(cam.viewUp || [0, 1, 0]);
  let u = _cross(up, nrm);
  if (_len(u) < 1e-6) {
    // up ∥ normal — достраиваем от той мировой оси, что меньше всего вдоль нормали
    const ax = Math.abs(nrm[0]) <= Math.abs(nrm[1]) && Math.abs(nrm[0]) <= Math.abs(nrm[2]) ? [1, 0, 0]
      : Math.abs(nrm[1]) <= Math.abs(nrm[2]) ? [0, 1, 0] : [0, 0, 1];
    u = _cross(ax, nrm);
  }
  const uDir = _norm(u);
  const vDir = _norm(_cross(nrm, uDir));   // гарантированно ⟂ uDir и в плоскости
  return { o, uDir, vDir };
}

/** Мировая диагональ дозовой сетки (по 8 углам). */
export function doseDiagonal(dose) {
  const g = dose.geom, C = dose.cols - 1, R = dose.rows - 1, F = dose.frames - 1;
  const mn = [Infinity, Infinity, Infinity], mx = [-Infinity, -Infinity, -Infinity];
  for (const c of [0, C]) for (const r of [0, R]) for (const f of [0, F]) {
    const w = doseGridPixelToWorld(g, c, r, f);
    for (let i = 0; i < 3; i++) { mn[i] = Math.min(mn[i], w[i]); mx[i] = Math.max(mx[i], w[i]); }
  }
  return Math.hypot(mx[0] - mn[0], mx[1] - mn[1], mx[2] - mn[2]);
}

/** Шаг и размер сетки выборки, покрывающей плоскость (крышка 500 — против фриза). */
export function samplingParams(dose, cap = 500) {
  const step = Math.max(0.5, Math.min(dose.geom.pixelSpacing[0], dose.geom.pixelSpacing[1], frameSpacing(dose.geom)));
  const n = Math.min(cap, Math.ceil(doseDiagonal(dose) / step) + 2);
  return { step, n };
}

/** Цвет уровня: высокая доза — красная, низкая — синяя (уровни отсортированы по убыванию). */
export function levelColor(idx, n) {
  const t = n > 1 ? idx / (n - 1) : 0;
  const r = Math.round(255 * (1 - t)), b = Math.round(255 * t), g = Math.round(120 * (1 - Math.abs(t - 0.5) * 2));
  return `rgb(${r},${g},${b})`;
}

/**
 * Полилинии изодоз в КАНВЕННЫХ координатах для одного окна.
 *
 * @param {object} dose объект дозы (getDose())
 * @param {object} cam {focalPoint, viewUp, viewPlaneNormal}
 * @param {(w:number[])=>number[]|null} worldToCanvas проекция окна (инжектируется)
 * @param {number[]} levelsGy уровни, ОТСОРТИРОВАННЫЕ ПО УБЫВАНИЮ
 * @param {{mono?:string, cap?:number}} [opts]
 * @returns {Array<{gy:number, color:string, lines:number[][][]}>} lines: список полилиний [x,y]
 */
export function isodoseCanvasPolylines(dose, cam, worldToCanvas, levelsGy, opts = {}) {
  const { o, uDir, vDir } = planeBasisFromCamera(cam);
  const { step, n } = samplingParams(dose, opts.cap || 500);
  const out = [];
  levelsGy.forEach((lv, li) => {
    const worldLines = isodoseOnPlane(dose, o, uDir, vDir, n, n, step, lv);
    const color = opts.mono || levelColor(li, levelsGy.length);
    const lines = [];
    for (const line of worldLines) {
      const pts = [];
      for (const w of line) {
        const cv = worldToCanvas(w);
        if (!cv || !Number.isFinite(cv[0]) || !Number.isFinite(cv[1])) continue;
        pts.push([cv[0], cv[1]]);
      }
      if (pts.length >= 2) lines.push(pts);
    }
    if (lines.length) out.push({ gy: lv, color, lines });
  });
  return out;
}

export default { planeBasisFromCamera, doseDiagonal, samplingParams, levelColor, isodoseCanvasPolylines };
